import { roundMoney } from '../../utils/money';
import {
  buildFreightLine,
  buildFreightTotals,
  sumFreightLineCosts,
  type FreightLineRow,
  type FreightLineUnitRow,
  type FreightLinesTotals,
  type FreightTotalsUnit,
  type LoadUnit,
} from './freightGoodsTable';
import type { TransportSupplierOrderLoadItemDto, TransportSupplierOrderSummaryDto } from '../../types/transport';

/**
 * INBOUND truck: what each linked ZD loads, line by line, and the part of the
 * truck's cost that falls on it. Read-only presentation of backend facts — the
 * money split is owned by `transportService.syncTransportCostsInTransaction`
 * (saved `StockFreightShare`) and, per line, by `stockValuationService`
 * (`freightPLN`); the quantities are what THIS truck carries of each line
 * (`carried`, Addendum A — the whole line on an older response), and a line
 * the truck does not carry (`NONE`) is left out. Rows, totals and averages
 * come from the shared `freightGoodsTable.ts`.
 */
export type { LoadUnit };
export type SupplierOrderLineUnitRow = FreightLineUnitRow;
export type SupplierOrderLineRow = FreightLineRow;
export type SupplierOrderTotalsUnit = FreightTotalsUnit;
export type SupplierOrderLinesTotals = FreightLinesTotals;

export interface SupplierOrderFreightRow {
  id: string;
  label: string;
  supplierName: string | null;
  /** `null` when the response carries no freight share (no cost access, or none saved). */
  share: {
    percent: number | null;
    amountPLN: number | null;
    originalAmount: number | null;
    currency: string | null;
  } | null;
  lines: SupplierOrderLineRow[];
  /** "Razem / Średnio" of the goods table; `null` when the ZD has no lines. */
  totals: SupplierOrderLinesTotals | null;
}

export interface SupplierOrderLoadTotals {
  pcs: number | null;
  m2: number | null;
  m3: number | null;
}

export interface SupplierOrderFreightSummary {
  rows: SupplierOrderFreightRow[];
  /** Sum of the saved shares in PLN; `null` when any ZD has no readable share. */
  totalAmountPLN: number | null;
  /** Whole-truck totals; a component is `null` when any line lacks it. */
  load: SupplierOrderLoadTotals;
  hasLoad: boolean;
}

/**
 * The line as this truck carries it: the carried quantity set replaces the
 * line's own set; lines the truck does not carry are dropped.
 */
export function carriedLoadItems(items: TransportSupplierOrderLoadItemDto[] = []): TransportSupplierOrderLoadItemDto[] {
  return items
    .filter((item) => item.carriedSource !== 'NONE')
    .map((item) => (item.carried
      ? {
        ...item,
        quantity: item.carried.quantity,
        quantityPcs: item.carried.quantityPcs,
        quantityM2: item.carried.quantityM2,
        quantityM3: item.carried.quantityM3,
      }
      : item));
}

function buildLine(item: TransportSupplierOrderLoadItemDto): SupplierOrderLineRow {
  return buildFreightLine(item);
}

function buildTotals(items: TransportSupplierOrderLoadItemDto[]): SupplierOrderLinesTotals | null {
  return buildFreightTotals(items, sumFreightLineCosts(items));
}

function sumComponent(items: TransportSupplierOrderLoadItemDto[], field: 'quantityPcs' | 'quantityM2' | 'quantityM3'): number | null {
  if (items.length === 0) return null;
  let total = 0;
  for (const item of items) {
    const value = item[field];
    if (value === null || value === undefined) return null;
    total += value;
  }
  return total;
}

export function summarizeSupplierOrderFreight(
  supplierOrders: TransportSupplierOrderSummaryDto[] | undefined,
): SupplierOrderFreightSummary {
  const orders = supplierOrders || [];
  const rows = orders.map((supplierOrder): SupplierOrderFreightRow => ({
    id: supplierOrder.id,
    label: supplierOrder.displayOrderNumber || supplierOrder.orderNumber,
    supplierName: supplierOrder.supplierName,
    share: supplierOrder.freightShare
      ? {
        percent: supplierOrder.freightShare.percent,
        amountPLN: supplierOrder.freightShare.amountPLN,
        originalAmount: supplierOrder.freightShare.originalAmount,
        currency: supplierOrder.freightShare.currency,
      }
      : null,
    lines: carriedLoadItems(supplierOrder.loadItems).map(buildLine),
    totals: buildTotals(carriedLoadItems(supplierOrder.loadItems)),
  }));

  const allItems = orders.flatMap((supplierOrder) => carriedLoadItems(supplierOrder.loadItems));
  return {
    rows,
    totalAmountPLN: rows.length > 0 && rows.every((row) => row.share?.amountPLN != null)
      ? roundMoney(rows.reduce((sum, row) => sum + (row.share?.amountPLN ?? 0), 0))
      : null,
    load: {
      pcs: sumComponent(allItems, 'quantityPcs'),
      m2: sumComponent(allItems, 'quantityM2'),
      m3: sumComponent(allItems, 'quantityM3'),
    },
    hasLoad: allItems.length > 0,
  };
}
